/**
 * OAuth 2.0 authorization-code flow helpers: `state` and PKCE (RFC 7636, S256).
 *
 * Why: without `state` the callback accepts a code started anywhere (login CSRF), and without
 * PKCE an intercepted code can be redeemed by whoever holds it (011 finding 4).
 * How: `beginLogin` stores a random state and verifier and returns the authorize URL carrying
 * the state and the verifier's SHA-256 challenge; `completeLogin` checks the returned state
 * and hands back the verifier for the token exchange. Both values are single-use: they are
 * removed on every completion attempt, success or failure. Dependency-free — Web Crypto only —
 * and storage is injected so the logic is unit-testable outside the browser.
 */

/** The subset of the Web Storage API these helpers use (window.sessionStorage in the app). */
export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Where and for whom the authorization request is made. */
export interface AuthorizeConfig {
  /** Full URL of the Cognito `/oauth2/authorize` endpoint. */
  authorizeEndpoint: string;
  clientId: string;
  redirectUri: string;
}

const STATE_KEY = 'hallway_oauth_state';
const VERIFIER_KEY = 'hallway_oauth_verifier';

/** Base64url without padding, as RFC 7636 requires. */
function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach(b => { binary += String.fromCharCode(b); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 32 random bytes, base64url-encoded: 43 characters, 256 bits of entropy. */
function randomToken(): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(32)));
}

/**
 * Compute the PKCE S256 code challenge for a verifier.
 *
 * @param verifier - The code verifier (43–128 unreserved characters).
 * @returns base64url(SHA-256(verifier)).
 */
export async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

/**
 * Start a login: store fresh state and verifier, and build the authorize URL.
 *
 * @param store - Tab-scoped storage for the single-use values.
 * @param config - Authorize endpoint, client id and redirect URI.
 * @returns The URL to navigate to.
 */
export async function beginLogin(store: KeyValueStore, config: AuthorizeConfig): Promise<string> {
  const state = randomToken();
  const verifier = randomToken();
  store.setItem(STATE_KEY, state);
  store.setItem(VERIFIER_KEY, verifier);
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    scope: 'openid email profile',
    state,
    code_challenge: await pkceChallenge(verifier),
    code_challenge_method: 'S256',
  });
  return `${config.authorizeEndpoint}?${params}`;
}

/**
 * Validate a callback and release the verifier for the token exchange.
 *
 * @param store - The storage `beginLogin` wrote to.
 * @param params - The callback URL's query parameters.
 * @returns The authorization code and the matching verifier.
 * @throws Error when the server returned an error, the code is missing, no login was started
 *   in this tab, or the state does not match.
 */
export function completeLogin(store: KeyValueStore, params: URLSearchParams): { code: string; verifier: string } {
  const expectedState = store.getItem(STATE_KEY);
  const verifier = store.getItem(VERIFIER_KEY);
  // Single-use: clear before any check so neither a failure nor a replay can reuse them.
  store.removeItem(STATE_KEY);
  store.removeItem(VERIFIER_KEY);

  const error = params.get('error');
  if (error) throw new Error(`Sign-in failed: ${error}`);
  const code = params.get('code');
  const state = params.get('state');
  if (!expectedState || !verifier) throw new Error('No sign-in was started in this tab. Please sign in again.');
  if (!state || state !== expectedState) throw new Error('Sign-in state mismatch. Please sign in again.');
  if (!code) throw new Error('Sign-in response had no authorization code.');
  return { code, verifier };
}
